<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { usePromotionsStore } from '../stores/promotionsStore'

const router = useRouter()
const promotionsStore = usePromotionsStore()
const isDismissed = ref(false)

const campaign = computed(() => {
  if (promotionsStore.activePaydayCampaign) {
    return promotionsStore.activePaydayCampaign
  }
  if (promotionsStore.activePromotions && promotionsStore.activePromotions.length > 0) {
    return promotionsStore.activePromotions[0]
  }
  return null
})

// Countdown Timer state
const days = ref('00')
const hours = ref('00')
const minutes = ref('00')
const seconds = ref('00')
let timerInterval = null

// Valid countdown condition: must have end_date AND end_date must be in the future
const hasValidCountdown = computed(() => {
  if (!campaign.value || !campaign.value.end_date) return false
  const end = new Date(campaign.value.end_date).getTime()
  if (isNaN(end)) return false
  const now = new Date().getTime()
  return end > now
})

const updateCountdown = () => {
  if (!hasValidCountdown.value) return

  const end = new Date(campaign.value.end_date).getTime()
  const now = new Date().getTime()
  const diff = end - now

  if (diff <= 0) {
    days.value = '00'
    hours.value = '00'
    minutes.value = '00'
    seconds.value = '00'
    return
  }

  const d = Math.floor(diff / (1000 * 60 * 60 * 24))
  const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const s = Math.floor((diff % (1000 * 60)) / 1000)

  days.value = String(d).padStart(2, '0')
  hours.value = String(h).padStart(2, '0')
  minutes.value = String(m).padStart(2, '0')
  seconds.value = String(s).padStart(2, '0')
}

const handleVisibilityChange = () => {
  if (document.hidden) {
    if (timerInterval) {
      clearInterval(timerInterval)
      timerInterval = null
    }
  } else {
    updateCountdown()
    if (!timerInterval) {
      timerInterval = setInterval(updateCountdown, 1000)
    }
  }
}

onMounted(() => {
  promotionsStore.fetchProductPromos()
  updateCountdown()
  timerInterval = setInterval(updateCountdown, 1000)
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
  if (timerInterval) clearInterval(timerInterval)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})

const badgeText = computed(() => {
  if (!campaign.value) return 'PAYDAY SALE'
  return campaign.value.badge_text || campaign.value.title || 'PAYDAY SALE'
})

const bannerBgClass = computed(() => {
  if (!campaign.value) return 'bg-gradient-to-r from-red-700 via-rose-600 to-amber-500 border-amber-300/30'
  const style = campaign.value.frame_style || ''
  if (style === 'payday_orange') {
    return 'bg-gradient-to-r from-amber-600 via-orange-600 to-amber-500 border-amber-300/30'
  } else if (style === 'gold_vip') {
    return 'bg-gradient-to-r from-zinc-950 via-amber-900 to-zinc-950 border-amber-400/40'
  } else if (style === 'neon_cyber') {
    return 'bg-gradient-to-r from-slate-950 via-cyan-900 to-purple-950 border-cyan-400/40'
  } else if (style === 'purple_flash') {
    return 'bg-gradient-to-r from-purple-950 via-purple-800 to-indigo-900 border-purple-400/40'
  }
  return 'bg-gradient-to-r from-red-700 via-rose-600 to-amber-500 border-amber-300/30'
})
</script>

<template>
  <div v-if="campaign && !isDismissed" 
    class="relative w-full z-50 text-white shadow-md border-b transition-all duration-300"
    :class="bannerBgClass"
  >
    <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-2 flex flex-wrap items-center justify-between gap-3 text-xs md:text-sm font-sans">
      
      <!-- Left Info Badge & Description -->
      <div class="flex items-center gap-2.5 font-bold min-w-0">
        <span class="inline-flex items-center gap-1.5 bg-amber-400 text-red-950 px-3 py-1 rounded-full font-black text-[11px] uppercase tracking-wider shadow-md flex-shrink-0">
          <svg class="w-3.5 h-3.5 text-red-950 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-1.048c-2.479 1.157-4.453 3.321-4.996 6.002a7.65 7.65 0 01-.63-1.637 1 1 0 00-1.897.433c.69 3.03 2.68 5.753 5.485 6.967A6.985 6.985 0 0010 14a6.985 6.985 0 006.1-3.567c.71-1.32.9-2.85.5-4.3a7.485 7.485 0 00-4.205-3.58z" clip-rule="evenodd"/>
          </svg>
          <span>{{ badgeText }}</span>
        </span>
        
        <span class="hidden sm:inline-block text-amber-100 font-semibold text-xs md:text-sm truncate max-w-xs md:max-w-xl">
          {{ campaign.description || 'สิทธิพิเศษราคาสุดคุ้มเฉพาะช่วงเวลาแคมเปญนี้เท่านั้น!' }}
        </span>
      </div>

      <!-- Right Countdown & CTA Button -->
      <div class="flex items-center gap-3 ml-auto sm:ml-0 flex-shrink-0">
        
        <!-- Live Real-Time Countdown (Rendered ONLY if hasValidCountdown is true) -->
        <div v-if="hasValidCountdown" class="flex items-center gap-1.5 font-mono font-bold text-xs bg-black/35 backdrop-blur-md px-2.5 py-1 rounded-xl border border-white/20 shadow-inner">
          <span class="text-amber-200 hidden md:inline text-[11px] font-sans font-extrabold mr-0.5">จบโปรใน:</span>
          
          <template v-if="Number(days) > 0">
            <span class="bg-amber-400 text-red-950 font-black px-1.5 py-0.5 rounded text-[11px]">{{ days }}วัน</span>
          </template>

          <span class="bg-black/50 px-1.5 py-0.5 rounded border border-white/10 text-amber-300 font-black text-xs min-w-[24px] text-center">{{ hours }}</span>
          <span class="text-amber-300 font-black animate-pulse">:</span>
          <span class="bg-black/50 px-1.5 py-0.5 rounded border border-white/10 text-amber-300 font-black text-xs min-w-[24px] text-center">{{ minutes }}</span>
          <span class="text-amber-300 font-black animate-pulse">:</span>
          <span class="bg-black/50 px-1.5 py-0.5 rounded border border-white/10 text-amber-300 font-black text-xs min-w-[24px] text-center">{{ seconds }}</span>
        </div>

        <!-- Action Button -->
        <router-link to="/promotions" class="px-3.5 py-1.5 bg-amber-400 hover:bg-amber-300 text-red-950 font-black rounded-xl text-xs transition-all shadow-md hover:shadow-lg active:scale-95 flex items-center gap-1.5">
          <span>ช้อปเลย!</span>
          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
          </svg>
        </router-link>

        <!-- Close button -->
        <button @click="isDismissed = true" class="text-white/70 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors" title="ปิดแบนเนอร์">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

    </div>
  </div>
</template>
